import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  DollarSign,
  Calendar,
  TrendingUp,
} from "lucide-react";

interface Proposal {
  id: string;
  title: string;
  client: string;
  value: number;
  status: "Draft" | "Sent" | "Accepted" | "Rejected";
  sentDate: string | null;
  validUntil: string;
  version: string;
}

interface ViewProposalDialogProps {
  proposal: Proposal | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ViewProposalDialog({ proposal, open, onOpenChange }: ViewProposalDialogProps) {
  if (!proposal) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Accepted":
        return "bg-green-100 text-green-800 border-green-200";
      case "Rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "Sent":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Draft":
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  const DetailRow = ({ label, value }: { label: string; value: string | number | React.ReactNode }) => (
    <div className="flex items-center gap-3 py-2">
      <div className="flex-1">
        <span className="text-sm font-medium text-gray-600">{label}:</span>
        <span className="ml-2 text-sm text-gray-900">{value}</span>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] h-[90vh] flex flex-col p-0 rounded-xl overflow-hidden">
        <DialogHeader className="px-6 pb-0 pt-6">
          <DialogTitle className="sr-only">Proposal Details - {proposal.title}</DialogTitle>
          <DialogDescription className="sr-only">View proposal information and metrics.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 shadow-sm">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl font-bold text-gray-900">{proposal.title}</h1>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge className={getStatusColor(proposal.status)}>
                      {proposal.status}
                    </Badge>
                    <span className="text-sm text-gray-600">Client: {proposal.client}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-600">Proposal Value</p>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(proposal.value)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-600">Version</p>
                    <p className="text-2xl font-bold text-blue-700">v{proposal.version}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Proposal Information
              </h2>
              <div className="space-y-3">
                <DetailRow label="Client" value={proposal.client} />
                <DetailRow label="Status" value={<Badge className={getStatusColor(proposal.status)}>{proposal.status}</Badge>} />
              </div>
            </div>

            {/* Dates */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Timeline
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-gray-600">Sent Date</p>
                  <p className="text-sm text-gray-900">{proposal.sentDate || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Valid Until</p>
                  <p className="text-sm text-gray-900">{proposal.validUntil}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


